import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import ScrollableDialog from './ScrollableDialog';
import { STRIPE_PUBLISHABLE_KEY } from '../../config/runtimeConfig';
import { BRAND } from '../../theme/brandTokens';

// One Stripe.js instance per account: store checkouts are direct charges created on the store's
// connected account, and Stripe.js only finds a session on the account it was opened with.
const stripePromises = new Map();
export const getStripe = (stripeAccountId) => {
  const key = stripeAccountId || '';
  if (!stripePromises.has(key)) {
    stripePromises.set(
      key,
      stripeAccountId
        ? loadStripe(STRIPE_PUBLISHABLE_KEY, { stripeAccount: stripeAccountId })
        : loadStripe(STRIPE_PUBLISHABLE_KEY),
    );
  }
  return stripePromises.get(key);
};

/**
 * Mounts a Stripe Embedded Checkout session inline, so the customer never
 * navigates away from the app to pay. `createSession` must resolve to
 * `{ clientSecret, stripeAccountId }` from one of the backend checkout-session endpoints.
 */
const StripeEmbeddedCheckoutDialog = ({
  open,
  onClose,
  createSession,
  onComplete,
  title,
  subtitle,
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const containerRef = useRef(null);
  const embeddedCheckoutRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const destroyEmbeddedCheckout = useCallback(() => {
    if (embeddedCheckoutRef.current) {
      try {
        embeddedCheckoutRef.current.destroy();
      } catch (err) {
        console.error('Failed to destroy embedded checkout:', err);
      }
      embeddedCheckoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!open) {
      destroyEmbeddedCheckout();
      return undefined;
    }

    let cancelled = false;

    const mountCheckout = async () => {
      setError('');
      setLoading(true);
      try {
        const session = await createSession();
        const stripe = await getStripe(session?.stripeAccountId);
        if (cancelled || !containerRef.current) {
          return;
        }

        const embeddedCheckout = await stripe.createEmbeddedCheckoutPage({
          clientSecret: session?.clientSecret,
          onComplete: () => {
            onComplete?.();
          },
        });

        if (cancelled) {
          embeddedCheckout.destroy();
          return;
        }

        embeddedCheckoutRef.current = embeddedCheckout;
        embeddedCheckout.mount(containerRef.current);
      } catch (err) {
        console.error('Failed to start embedded checkout:', err);
        if (!cancelled) {
          setError(t('stripeCheckout.error'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    mountCheckout();

    return () => {
      cancelled = true;
      destroyEmbeddedCheckout();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    destroyEmbeddedCheckout();
    onClose?.();
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={handleClose}
      title={title ?? t('stripeCheckout.title')}
      subtitle={subtitle}
      showCloseButton
      fullScreen={isMobile}
      maxWidth="md"
      paperBorderRadius={isMobile ? 0 : '17px'}
      titleSx={{ flexShrink: 0 }}
      contentSx={{
        // Cap the body itself rather than the dialog. Stripe's iframe grows as the customer
        // switches payment method, and bounding the scrolling element directly means it
        // scrolls whatever the surrounding flex column resolves to — leaving the dialog to
        // stay as short as the content allows. The offsets leave room for the title bar and,
        // on desktop, the dialog's own margins.
        maxHeight: { xs: 'calc(100vh - 88px)', sm: 'calc(100vh - 168px)' },
        minHeight: 0,
        px: { xs: 0, sm: 2 },
        py: 0,
      }}
    >
      {error ? (
        <Box sx={{ px: { xs: 2, sm: 1 }, py: 3 }}>
          <Typography color="error" variant="body2">{error}</Typography>
        </Box>
      ) : (
        <>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320, py: 4 }}>
              <CircularProgress sx={{ color: BRAND.accent }} />
            </Box>
          ) : null}
          <Box ref={containerRef} />
        </>
      )}
    </ScrollableDialog>
  );
};

export default StripeEmbeddedCheckoutDialog;
